// Performance monitoring utilities for Core Web Vitals and custom metrics
export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  url: string;
  userAgent: string;
}

export interface CoreWebVitals {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];
  private isEnabled: boolean = true;

  constructor() {
    if (typeof window === 'undefined' || import.meta.env.DEV) {
      this.isEnabled = false;
      return;
    }

    this.initializeObservers();
    this.trackNavigationTiming();
  }

  private initializeObservers() {
    try {
      // Largest Contentful Paint (LCP)
      if ('PerformanceObserver' in window) {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          if (lastEntry) {
            this.recordMetric('LCP', lastEntry.startTime);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            const fid = entry.processingStart - entry.startTime;
            this.recordMetric('FID', fid);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);

        // Cumulative Layout Shift (CLS)
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.recordMetric('CLS', clsValue);
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.push(clsObserver);

        // First Contentful Paint (FCP)
        const fcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (entry.name === 'first-contentful-paint') {
              this.recordMetric('FCP', entry.startTime);
            }
          });
        });
        fcpObserver.observe({ entryTypes: ['paint'] });
        this.observers.push(fcpObserver);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[PerformanceMonitor] Failed to initialize observers:', error);
      }
    }
  }

  private trackNavigationTiming() {
    if (!('performance' in window) || !window.performance.timing) return;

    // Wait for page load to complete
    window.addEventListener('load', () => {
      setTimeout(() => {
        const timing = window.performance.timing;
        const navigation = window.performance.navigation;

        // Time to First Byte (TTFB)
        const ttfb = timing.responseStart - timing.navigationStart;
        this.recordMetric('TTFB', ttfb);

        // DOM Content Loaded
        const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
        this.recordMetric('DOM_CONTENT_LOADED', domContentLoaded);

        // Page Load Time
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        this.recordMetric('PAGE_LOAD_TIME', pageLoadTime);

        // DNS Lookup Time
        const dnsTime = timing.domainLookupEnd - timing.domainLookupStart;
        this.recordMetric('DNS_LOOKUP_TIME', dnsTime);

        // Connection Time
        const connectionTime = timing.connectEnd - timing.connectStart;
        this.recordMetric('CONNECTION_TIME', connectionTime);

        // Navigation Type
        this.recordMetric('NAVIGATION_TYPE', navigation.type);
      }, 0);
    });
  }

  public recordMetric(name: string, value: number) {
    if (!this.isEnabled) return;

    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    this.metrics.push(metric);

    // Log in development
    if (import.meta.env.DEV) {
      console.log(`[Performance] ${name}: ${value.toFixed(2)}ms`);
    }

    // Send to analytics in production (implement your analytics service)
    this.sendToAnalytics(metric);
  }

  public recordCustomTiming(name: string, startTime: number, endTime?: number) {
    const end = endTime || performance.now();
    const duration = end - startTime;
    this.recordMetric(name, duration);
  }

  public startTiming(name: string): () => void {
    const startTime = performance.now();
    return () => {
      this.recordCustomTiming(name, startTime);
    };
  }

  public getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  public getCoreWebVitals(): CoreWebVitals {
    const vitals: CoreWebVitals = {};
    
    this.metrics.forEach(metric => {
      switch (metric.name) {
        case 'LCP':
          vitals.lcp = metric.value;
          break;
        case 'FID':
          vitals.fid = metric.value;
          break;
        case 'CLS':
          vitals.cls = metric.value;
          break;
        case 'FCP':
          vitals.fcp = metric.value;
          break;
        case 'TTFB':
          vitals.ttfb = metric.value;
          break;
      }
    });

    return vitals;
  }

  public getPerformanceScore(): number {
    const vitals = this.getCoreWebVitals();
    let score = 100;

    // LCP scoring (Good: <2.5s, Needs Improvement: 2.5-4s, Poor: >4s)
    if (vitals.lcp) {
      if (vitals.lcp > 4000) score -= 30;
      else if (vitals.lcp > 2500) score -= 15;
    }

    // FID scoring (Good: <100ms, Needs Improvement: 100-300ms, Poor: >300ms)
    if (vitals.fid) {
      if (vitals.fid > 300) score -= 25;
      else if (vitals.fid > 100) score -= 10;
    }

    // CLS scoring (Good: <0.1, Needs Improvement: 0.1-0.25, Poor: >0.25)
    if (vitals.cls) {
      if (vitals.cls > 0.25) score -= 25;
      else if (vitals.cls > 0.1) score -= 10;
    }

    return Math.max(0, score);
  }

  private sendToAnalytics(metric: PerformanceMetric) {
    // Implement your analytics service here
    // Example: Google Analytics, custom endpoint, etc.
    
    // For now, store in localStorage for debugging
    try {
      const stored = localStorage.getItem('performance_metrics');
      const metrics = stored ? JSON.parse(stored) : [];
      metrics.push(metric);
      
      // Keep only last 100 metrics
      if (metrics.length > 100) {
        metrics.splice(0, metrics.length - 100);
      }
      
      localStorage.setItem('performance_metrics', JSON.stringify(metrics));
    } catch (error) {
      // Ignore localStorage errors
    }
  }

  public generateReport(): string {
    const vitals = this.getCoreWebVitals();
    const score = this.getPerformanceScore();
    
    return `
Performance Report for ${window.location.href}
Generated: ${new Date().toISOString()}
Overall Score: ${score}/100

Core Web Vitals:
- LCP (Largest Contentful Paint): ${vitals.lcp ? `${vitals.lcp.toFixed(2)}ms` : 'Not measured'}
- FID (First Input Delay): ${vitals.fid ? `${vitals.fid.toFixed(2)}ms` : 'Not measured'}
- CLS (Cumulative Layout Shift): ${vitals.cls ? vitals.cls.toFixed(3) : 'Not measured'}
- FCP (First Contentful Paint): ${vitals.fcp ? `${vitals.fcp.toFixed(2)}ms` : 'Not measured'}
- TTFB (Time to First Byte): ${vitals.ttfb ? `${vitals.ttfb.toFixed(2)}ms` : 'Not measured'}

Recommendations:
${score < 70 ? '⚠️ Performance needs improvement' : score < 90 ? '✅ Good performance' : '🚀 Excellent performance'}
    `.trim();
  }

  public destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics = [];
    this.isEnabled = false;
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// React hook for performance monitoring
export function usePerformanceMonitor() {
  const startTiming = (name: string) => performanceMonitor.startTiming(name);
  const recordMetric = (name: string, value: number) => performanceMonitor.recordMetric(name, value);
  const getMetrics = () => performanceMonitor.getMetrics();
  const getCoreWebVitals = () => performanceMonitor.getCoreWebVitals();
  const getPerformanceScore = () => performanceMonitor.getPerformanceScore();
  const generateReport = () => performanceMonitor.generateReport();

  return {
    startTiming,
    recordMetric,
    getMetrics,
    getCoreWebVitals,
    getPerformanceScore,
    generateReport
  };
}